<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useCallsStore } from 'dashboard/stores/calls';
import { useCallHistoryStore } from 'dashboard/stores/callHistory';
import { VOICE_CALL_PROVIDERS } from 'dashboard/helper/inbox';
import { VOICE_CALL_DIRECTION } from 'dashboard/components-next/message/constants';
import FreeswitchVoiceClient from 'dashboard/api/channel/voice/freeswitchVoiceClient';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import CallsEmptyState from 'dashboard/components-next/Calls/CallsEmptyState.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { emitter } from 'shared/helpers/mitt';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();
const callsStore = useCallsStore();
const callHistoryStore = useCallHistoryStore();

const inboxes = useMapGetter('inboxes/getInboxes');
const contactsList = useMapGetter('contacts/getContacts');
const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const isVoiceEnabled = computed(() => {
  return isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.FREESWITCH);
});

const phoneInputRef = ref(null);
const phoneNumber = ref('');
const searchQuery = ref('');
const activeTab = ref('recent'); // 'recent' | 'contacts'
const isDialing = ref(false);
const isWebRTCReady = ref(false);
const showSimPickerDialog = ref(false);

const currentAccountId = computed(() => {
  return store.getters.getCurrentAccountId || store.getters.getCurrentAccount?.id || 220;
});

// Strictly filter to voice / telephony lines ONLY (Never include Facebook, WhatsApp, Email, etc.)
const voiceInboxes = computed(() => {
  const all = inboxes.value || [];
  const lines = [];

  all.forEach(inbox => {
    const chType = inbox.channel_type || inbox.channelType;
    const provider = inbox.provider || inbox.channel?.provider;
    const telNum = inbox.telephony_number || inbox.telephonyNumber;
    const telNums = inbox.telephony_numbers || [];

    if (telNums.length > 0) {
      telNums.forEach(tn => {
        const rawCli = tn.phone_number || tn;
        const cleanCli = String(rawCli).replace(/[^\d+]/g, '');
        lines.push({
          id: inbox.id,
          inboxId: inbox.id,
          name: inbox.name,
          simLabel: `Line ${lines.length + 1}`,
          displayNumber: cleanCli || rawCli,
          displayName: `${cleanCli || rawCli} (${inbox.name})`,
          provider: 'freeswitch',
          callerId: cleanCli || rawCli,
        });
      });
    } else if (
      telNum ||
      provider === 'freeswitch' ||
      chType === 'Channel::Telephony' ||
      chType === 'Channel::Voice' ||
      (inbox.name && (inbox.name.toLowerCase().includes('telephony') || inbox.name.toLowerCase().includes('voice') || inbox.name.toLowerCase().includes('freeswitch')))
    ) {
      const rawCli = telNum || inbox.phone_number || '';
      let cleanCli = String(rawCli).replace(/[^\d+]/g, '');
      if (!cleanCli && inbox.name) {
        const match = inbox.name.match(/\d[\d-]+/);
        if (match) cleanCli = match[0].replace(/[^\d+]/g, '');
      }
      lines.push({
        id: inbox.id,
        inboxId: inbox.id,
        name: inbox.name,
        simLabel: `Line ${lines.length + 1}`,
        displayNumber: cleanCli || inbox.name,
        displayName: cleanCli ? `${cleanCli} (${inbox.name})` : inbox.name,
        provider: 'freeswitch',
        callerId: cleanCli,
      });
    }
  });

  return lines;
});

const recentCalls = computed(() => callHistoryStore.records || []);
const isFetchingRecent = computed(() => callHistoryStore.uiFlags.isFetching);

const filteredContacts = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  const list = contactsList.value || [];
  if (!query) return list.slice(0, 40);
  return list.filter(c => {
    const name = (c.name || '').toLowerCase();
    const phone = (c.phoneNumber || c.phone_number || '').toLowerCase();
    return name.includes(query) || phone.includes(query);
  });
});

// Smart contact suggestions matching dialed number (iOS style)
const matchingSuggestions = computed(() => {
  const num = phoneNumber.value.trim().replace(/[^\d+]/g, '');
  if (!num || num.length < 2) return [];
  const list = contactsList.value || [];
  return list.filter(c => {
    const cp = (c.phoneNumber || c.phone_number || '').replace(/[^\d+]/g, '');
    const name = (c.name || '').toLowerCase();
    return (cp && (cp.includes(num) || cp.endsWith(num) || num.endsWith(cp))) || (name && name.includes(num.toLowerCase()));
  });
});

const topSuggestion = computed(() => matchingSuggestions.value[0] || null);
const moreSuggestionsCount = computed(() => Math.max(0, matchingSuggestions.value.length - 1));

// DTMF Audio Synthesizer via Web Audio API
let audioCtx = null;
const playDtmfTone = (digit) => {
  try {
    const dtmfFrequencies = {
      '1': [697, 1209],
      '2': [697, 1336],
      '3': [697, 1477],
      '4': [770, 1209],
      '5': [770, 1336],
      '6': [770, 1477],
      '7': [852, 1209],
      '8': [852, 1336],
      '9': [852, 1477],
      '*': [941, 1209],
      '0': [941, 1336],
      '#': [941, 1477],
    };

    const freqs = dtmfFrequencies[digit];
    if (!freqs) return;

    if (!audioCtx) {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass) audioCtx = new AudioContextClass();
    }
    if (!audioCtx) return;
    if (audioCtx.state === 'suspended') {
      audioCtx.resume();
    }

    const osc1 = audioCtx.createOscillator();
    const osc2 = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();

    osc1.type = 'sine';
    osc2.type = 'sine';
    osc1.frequency.setValueAtTime(freqs[0], audioCtx.currentTime);
    osc2.frequency.setValueAtTime(freqs[1], audioCtx.currentTime);

    gainNode.gain.setValueAtTime(0.08, audioCtx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.12);

    osc1.connect(gainNode);
    osc2.connect(gainNode);
    gainNode.connect(audioCtx.destination);

    osc1.start();
    osc2.start();
    osc1.stop(audioCtx.currentTime + 0.12);
    osc2.stop(audioCtx.currentTime + 0.12);
  } catch (e) {}
};

const dialpadKeys = [
  { digit: '1', sub: '' },
  { digit: '2', sub: 'ABC' },
  { digit: '3', sub: 'DEF' },
  { digit: '4', sub: 'GHI' },
  { digit: '5', sub: 'JKL' },
  { digit: '6', sub: 'MNO' },
  { digit: '7', sub: 'PQRS' },
  { digit: '8', sub: 'TUV' },
  { digit: '9', sub: 'WXYZ' },
  { digit: '*', sub: '' },
  { digit: '0', sub: '+' },
  { digit: '#', sub: '' },
];

let zeroLongPressTimer = null;
let longPressTriggered = false;

const handleDigitPressStart = (key) => {
  if (key.digit === '0') {
    longPressTriggered = false;
    zeroLongPressTimer = setTimeout(() => {
      longPressTriggered = true;
      phoneNumber.value += '+';
      playDtmfTone('0');
      zeroLongPressTimer = null;
    }, 500);
  }
};

const handleDigitPressEnd = (key) => {
  if (key.digit === '0') {
    if (zeroLongPressTimer) {
      clearTimeout(zeroLongPressTimer);
      zeroLongPressTimer = null;
    }
  }
};

const handleDigitClick = (key) => {
  if (key.digit === '0') {
    if (longPressTriggered) {
      longPressTriggered = false;
      return;
    }
  }
  phoneNumber.value += key.digit;
  playDtmfTone(key.digit);
};

const handleBackspace = () => {
  if (phoneNumber.value.length > 0) {
    phoneNumber.value = phoneNumber.value.slice(0, -1);
  }
};

const handleClearAll = () => {
  phoneNumber.value = '';
};

const setNumberOnly = (num) => {
  if (!num) return;
  phoneNumber.value = num.replace(/[^\d+]/g, '');
};

const handleGlobalKeyDown = (e) => {
  // If user is typing in a search bar or modal input, don't hijack keys
  if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') && e.target.id !== 'dialpad-phone-input') {
    return;
  }
  const key = e.key;
  if (/^[0-9*#]$/.test(key)) {
    phoneNumber.value += key;
    playDtmfTone(key);
  } else if (key === '+') {
    phoneNumber.value += '+';
  } else if (key === 'Backspace') {
    handleBackspace();
  } else if (key === 'Enter') {
    e.preventDefault();
    console.log('[Dialpad] Enter key pressed, triggering call');
    handleCallClick();
  }
};

const handleCallClick = (targetInbox = null) => {
  console.log('[Dialpad] handleCallClick called. Raw phoneNumber:', phoneNumber.value, 'targetInbox:', targetInbox, 'voiceInboxes count:', voiceInboxes.value?.length);
  const number = phoneNumber.value.trim();
  if (!number) {
    useAlert(t('CONTACT_PANEL.PHONE_NOT_FOUND') || 'Please enter a phone number to call');
    return;
  }

  // If a specific inbox was selected directly (e.g. from popup)
  if (targetInbox) {
    executeCall(targetInbox);
    return;
  }

  // If multiple voice lines are configured and none selected, choose the first or prompt
  const line = voiceInboxes.value?.[0] || null;
  executeCall(line);
};

const selectNumberAndCall = (inbox) => {
  showSimPickerDialog.value = false;
  executeCall(inbox);
};

const setNumberAndPromptCall = (num) => {
  if (!num) return;
  phoneNumber.value = num.replace(/[^\d+]/g, '');
  handleCallClick();
};

const executeCall = async (targetInbox = null) => {
  const number = phoneNumber.value.trim();
  console.log('[Dialpad] executeCall started for:', number, 'targetInbox:', targetInbox, 'isDialing:', isDialing.value);
  if (!number) return;
  if (isDialing.value) {
    console.warn('[Dialpad] executeCall ignored: already dialing');
    return;
  }

  // Clear any previous stale call state in store
  callsStore.clearActiveCall();

  const line = targetInbox || voiceInboxes.value.find(l => l.callerId) || voiceInboxes.value[0];
  const inboxId = line?.inboxId || line?.id;
  let callerId = line?.callerId || '';
  if (!callerId && line?.displayNumber) {
    callerId = String(line.displayNumber).replace(/[^\d+]/g, '');
  }
  if (!callerId && FreeswitchVoiceClient.credentials?.callerId) {
    callerId = FreeswitchVoiceClient.credentials.callerId;
  }
  const accountId = currentAccountId.value;

  console.log('[Dialpad] Initiating outbound call to:', number, { inboxId, callerId, accountId, line });

  // Match contact in contacts list
  const cleanNum = number.replace(/[^\d+]/g, '');
  const matchedContact = (contactsList.value || []).find(c => {
    const cp = (c.phoneNumber || c.phone_number || '').replace(/[^\d+]/g, '');
    return cp && cleanNum && (cp === cleanNum || cp.endsWith(cleanNum) || cleanNum.endsWith(cp));
  });

  isDialing.value = true;
  try {
    const callSid = await FreeswitchVoiceClient.makeCall(number, {
      inboxId,
      accountId,
      callerId,
      contactId: matchedContact?.id,
    });

    callsStore.addCall({
      callSid,
      callId: callSid,
      inboxId,
      accountId,
      senderId: store.getters.getCurrentUserID,
      contactId: matchedContact?.id,
      callDirection: VOICE_CALL_DIRECTION.OUTBOUND,
      provider: VOICE_CALL_PROVIDERS.FREESWITCH,
      caller: {
        name: matchedContact?.name || number,
        phone: number,
        avatar: matchedContact?.avatar,
      },
    });

    useAlert(t('CONTACT_PANEL.CALL_INITIATED') || `Calling ${number}...`);

    // Proactively refresh recent call history
    setTimeout(() => {
      callHistoryStore.fetchCalls({ page: 1 }).catch(() => {});
    }, 1200);
  } catch (err) {
    console.error('[Dialpad] Call error:', err);
    useAlert(err?.message || 'Failed to place call');
  } finally {
    isDialing.value = false;
  }
};

const onVoiceCallEvent = () => {
  callHistoryStore.fetchCalls({ page: 1 }).catch(() => {});
  store.dispatch('contacts/get', { page: 1 }).catch(() => {});
};

onMounted(async () => {
  window.addEventListener('keydown', handleGlobalKeyDown);
  emitter.on('voice_call:created', onVoiceCallEvent);
  emitter.on('voice_call:updated', onVoiceCallEvent);
  emitter.on('voice_call:ended', onVoiceCallEvent);

  if (!isVoiceEnabled.value) return;

  try {
    await Promise.all([
      store.dispatch('inboxes/get'),
      store.dispatch('contacts/get', { page: 1 }),
      callHistoryStore.fetchCalls({ page: 1 }),
    ]);
  } catch (e) {}

  try {
    const init = await FreeswitchVoiceClient.initialize();
    isWebRTCReady.value = !!init;
  } catch (e) {
    isWebRTCReady.value = false;
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleGlobalKeyDown);
  emitter.off('voice_call:created', onVoiceCallEvent);
  emitter.off('voice_call:updated', onVoiceCallEvent);
  emitter.off('voice_call:ended', onVoiceCallEvent);
});
</script>

<template>
  <CallsEmptyState v-if="!isVoiceEnabled" />
  <section
    v-else
    class="flex flex-col w-full h-full overflow-hidden bg-n-background dark:bg-n-black"
  >
    <div class="h-5 bg-n-background dark:bg-n-black shrink-0 w-full" />
    <main class="flex-1 overflow-y-auto lg:overflow-hidden px-6 bg-n-surface-1 rounded-t-2xl flex flex-col min-h-0">
      <div class="w-full mx-auto max-w-5xl flex-grow flex flex-col min-h-0 py-3">
        <!-- Top Header matching app standard -->
        <header class="flex items-center justify-between w-full h-[3.25rem] mb-3 pb-2 border-b border-n-weak shrink-0">
          <div class="flex items-center gap-3">
            <span class="text-xl font-medium truncate text-n-slate-12">
              Dialpad
            </span>
            <!-- SIP WebRTC Status Pill -->
            <div class="flex items-center gap-2 px-2.5 py-1 rounded-full bg-n-alpha-1 border border-n-weak text-xs font-medium text-n-slate-12">
              <span
                class="size-2 rounded-full"
                :class="isWebRTCReady ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'"
              />
              <span>{{ isWebRTCReady ? 'WebRTC SIP Registered' : 'SIP Connecting...' }}</span>
            </div>
          </div>

          <div class="flex items-center gap-2">
            <Button
              variant="outline"
              color="slate"
              size="sm"
              icon="i-lucide-history"
              label="Call Logs"
              class="cursor-pointer"
              @click="router.push({ name: 'calls_dashboard_index', params: { accountId: currentAccountId } })"
            />
          </div>
        </header>

        <!-- Main Content Area: 2-Column Responsive Layout -->
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 py-1 w-full flex-1 min-h-0 items-start">
          <!-- Left Column: iOS Style Phone Keypad (5 cols) - Fixed at top, aligned to top -->
          <section class="lg:col-span-6 xl:col-span-5 flex flex-col items-center justify-start shrink-0 self-start">
            <div class="w-full max-w-[340px] sm:max-w-[360px] flex flex-col items-center select-none pt-0 pb-2">
              
              <!-- Large Crisp Dialed Number Display (Clean text, absolutely NO grey rectangular input box) -->
              <div
                class="w-full flex flex-col items-center justify-center min-h-[48px] px-2 mb-1 cursor-pointer"
                @click="phoneInputRef?.focus()"
              >
                <div
                  v-if="phoneNumber"
                  class="text-3xl sm:text-4xl font-semibold tracking-wider text-n-slate-12 text-center font-mono select-all break-all"
                >
                  {{ phoneNumber }}
                </div>
                <div v-else class="text-base sm:text-lg font-normal text-n-slate-9/70 text-center select-none">
                  Type or click keys to dial
                </div>
                <!-- Offscreen input for keyboard listener -->
                <input
                  ref="phoneInputRef"
                  id="dialpad-phone-input"
                  v-model="phoneNumber"
                  type="text"
                  style="position: absolute; opacity: 0; pointer-events: none; width: 0; height: 0; border: none !important;"
                  autocomplete="off"
                />
              </div>

              <!-- Smart Contact Match Suggestion (iOS Style Pill directly under number) -->
              <div
                v-if="topSuggestion"
                class="w-full max-w-[300px] mb-2 bg-n-surface-2 dark:bg-n-solid-2 border border-n-weak rounded-2xl p-2 shadow-sm flex flex-col gap-1 transition-all"
              >
                <!-- Top match row -->
                <div
                  class="flex items-center justify-between gap-2 px-2.5 py-1 rounded-xl hover:bg-n-alpha-2 cursor-pointer transition-colors"
                  @click="setNumberAndPromptCall(topSuggestion.phoneNumber || topSuggestion.phone_number)"
                >
                  <div class="flex items-center gap-2 min-w-0">
                    <Avatar
                      :src="topSuggestion.avatar"
                      :name="topSuggestion.name"
                      :size="24"
                      class="shrink-0"
                    />
                    <span class="text-xs font-semibold text-n-slate-12 truncate">{{ topSuggestion.name }}</span>
                  </div>
                  <span class="text-xs font-mono font-medium text-n-slate-11 shrink-0">
                    {{ topSuggestion.phoneNumber || topSuggestion.phone_number }}
                  </span>
                </div>

                <!-- More results row -->
                <div
                  v-if="moreSuggestionsCount > 0"
                  class="flex items-center gap-1.5 px-2.5 py-0.5 text-[11px] font-medium text-n-teal-11 hover:text-n-teal-12 border-t border-n-weak/50 cursor-pointer transition-colors"
                  @click="activeTab = 'contacts'; searchQuery = phoneNumber"
                >
                  <Icon icon="i-lucide-search" class="size-3 text-n-teal-11" />
                  <span>{{ moreSuggestionsCount }} More Results</span>
                </div>
              </div>

              <!-- 3x4 Touch Keypad Grid (Circular iOS Buttons) -->
              <div class="grid grid-cols-3 gap-y-3 gap-x-6 sm:gap-x-7 w-full max-w-[280px] sm:max-w-[300px] my-1.5">
                <button
                  v-for="key in dialpadKeys"
                  :key="key.digit"
                  type="button"
                  class="group size-15 sm:size-[68px] rounded-full bg-n-surface-2 dark:bg-n-solid-2 border border-n-weak/70 hover:bg-n-alpha-2 hover:border-n-teal-9/40 active:scale-95 active:bg-n-teal-9/10 flex flex-col items-center justify-center transition-all duration-150 shadow-sm cursor-pointer mx-auto select-none"
                  @mousedown="handleDigitPressStart(key)"
                  @mouseup="handleDigitPressEnd(key)"
                  @touchstart.passive="handleDigitPressStart(key)"
                  @touchend.passive="handleDigitPressEnd(key)"
                  @click="handleDigitClick(key)"
                >
                  <span class="text-2xl sm:text-[26px] font-normal sm:font-medium text-n-slate-12 group-hover:text-n-teal-11 transition-colors leading-none pointer-events-none">
                    {{ key.digit }}
                  </span>
                  <span v-if="key.sub" class="text-[9px] sm:text-[10px] font-bold tracking-widest text-n-slate-10 group-hover:text-n-slate-12 transition-colors mt-0.5 pointer-events-none">
                    {{ key.sub }}
                  </span>
                </button>
              </div>

              <!-- Bottom Action Row: Centered Green Call Button & Right Backspace -->
              <div class="grid grid-cols-3 items-center w-full max-w-[280px] sm:max-w-[300px] mt-2">
                <div class="flex items-center justify-center">
                  <button
                    v-if="phoneNumber"
                    type="button"
                    class="px-2 py-1 text-xs font-semibold text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-ruby-3/20 rounded-lg transition-colors cursor-pointer"
                    @click="handleClearAll"
                  >
                    Clear
                  </button>
                </div>

                <!-- Solid Centered Circular Green Call Button (100% visible iOS green) -->
                <button
                  type="button"
                  style="background-color: #22c55e !important; color: #ffffff !important;"
                  class="size-15 sm:size-[68px] rounded-full hover:brightness-110 active:scale-95 shadow-lg shadow-emerald-500/30 flex items-center justify-center text-white transition-all duration-150 cursor-pointer mx-auto select-none"
                  title="Call"
                  @click="handleCallClick()"
                >
                  <Spinner v-if="isDialing" :size="26" class="text-white pointer-events-none" />
                  <Icon v-else icon="i-lucide-phone" class="size-7 sm:size-8 text-white pointer-events-none" />
                </button>

                <!-- Right Backspace Button -->
                <div class="flex items-center justify-center">
                  <button
                    v-if="phoneNumber"
                    type="button"
                    class="size-11 sm:size-12 rounded-full hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-ruby-11 active:scale-90 flex items-center justify-center transition-all cursor-pointer"
                    title="Backspace"
                    @click="handleBackspace"
                  >
                    <Icon icon="i-lucide-delete" class="size-5 sm:size-6" />
                  </button>
                </div>
              </div>

            </div>
          </section>

          <!-- Right Column: Quick Contacts & Recent Calls Panel (7 cols) - Scrolls independently -->
          <section class="lg:col-span-6 xl:col-span-7 flex flex-col h-[calc(100vh-140px)] max-h-[720px] bg-n-solid-1 rounded-3xl border border-n-weak shadow-none overflow-hidden min-h-0">
            <!-- Sub Header & Tabs -->
            <div class="flex items-center justify-between px-6 py-3 border-b border-n-weak/80 bg-n-surface-1/40 gap-4 shrink-0">
              <div class="inline-flex items-center h-8 p-0.5 bg-n-surface-2 rounded-xl border border-n-weak shrink-0">
                <button
                  type="button"
                  class="h-7 px-3.5 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center justify-center"
                  :class="activeTab === 'recent' ? 'bg-n-brand text-white shadow-sm' : 'text-n-slate-11 hover:text-n-slate-12'"
                  @click="activeTab = 'recent'"
                >
                  Recent Calls
                </button>
                <button
                  type="button"
                  class="h-7 px-3.5 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center justify-center"
                  :class="activeTab === 'contacts' ? 'bg-n-brand text-white shadow-sm' : 'text-n-slate-11 hover:text-n-slate-12'"
                  @click="activeTab = 'contacts'"
                >
                  Contacts List
                </button>
              </div>

              <!-- Search Input aligned with tabs -->
              <div class="w-48 sm:w-60 flex items-center">
                <Input
                  v-model="searchQuery"
                  type="search"
                  placeholder="Search..."
                  :custom-input-class="[
                    'h-8 [&:not(.focus)]:!border-transparent bg-n-surface-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8 text-xs',
                  ]"
                  class="w-full !mb-0"
                >
                  <template #prefix>
                    <Icon
                      icon="i-lucide-search"
                      class="absolute -translate-y-1/2 text-n-slate-11 size-3.5 top-1/2 ltr:left-2.5 rtl:right-2.5 pointer-events-none"
                    />
                  </template>
                </Input>
              </div>
            </div>

        <!-- Tab Content -->
        <div class="flex-1 overflow-y-auto p-4">
          <!-- 1. Recent Calls Tab -->
          <div v-if="activeTab === 'recent'" class="flex flex-col gap-2">
            <div v-if="isFetchingRecent" class="flex items-center justify-center py-12">
              <Spinner :size="24" />
            </div>
            <div v-else-if="!recentCalls.length" class="flex flex-col items-center justify-center py-16 text-center text-n-slate-10">
              <Icon icon="i-lucide-phone-missed" class="size-10 mb-2 opacity-50" />
              <p class="text-sm font-medium">No recent calls recorded yet</p>
              <p class="text-xs">Outbound and inbound calls will appear here</p>
            </div>
            <div
              v-for="call in recentCalls"
              :key="call.id"
              class="flex items-center justify-between p-3 rounded-2xl bg-n-surface-1/70 dark:bg-n-surface-1 border border-n-weak/50 hover:border-n-brand/30 hover:bg-n-alpha-1 transition-all group"
            >
              <div class="flex items-center gap-3 min-w-0 cursor-pointer flex-1" @click="setNumberOnly(call.contact?.phoneNumber || call.contact?.phone_number)">
                <Avatar
                  :src="call.contact?.avatar"
                  :name="call.contact?.name || call.contact?.phoneNumber || 'Customer'"
                  :size="40"
                  class="shrink-0"
                />
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium text-n-slate-12 truncate">
                    {{ call.contact?.name || call.contact?.phoneNumber || 'Unknown' }}
                  </p>
                  <div class="flex items-center gap-1.5 text-xs text-n-slate-10">
                    <Icon
                      :icon="call.direction === 'outbound' ? 'i-lucide-phone-outgoing' : 'i-lucide-phone-incoming'"
                      class="size-3 text-n-teal-11"
                    />
                    <span>{{ call.contact?.phoneNumber || call.contact?.phone_number || '' }}</span>
                  </div>
                </div>
              </div>

              <!-- Action buttons -->
              <div class="flex items-center gap-2 shrink-0">
                <Button
                  icon="i-lucide-phone"
                  teal
                  size="sm"
                  class="!rounded-xl"
                  @click="setNumberAndPromptCall(call.contact?.phoneNumber || call.contact?.phone_number)"
                />
              </div>
            </div>
          </div>

          <!-- 2. Contacts List Tab -->
          <div v-else class="flex flex-col gap-2">
            <div v-if="!filteredContacts.length" class="flex flex-col items-center justify-center py-16 text-center text-n-slate-10">
              <Icon icon="i-lucide-users" class="size-10 mb-2 opacity-50" />
              <p class="text-sm font-medium">No contacts match your search</p>
            </div>
            <div
              v-for="contact in filteredContacts"
              :key="contact.id"
              class="flex items-center justify-between p-3 rounded-2xl bg-n-surface-1/70 dark:bg-n-surface-1 border border-n-weak/50 hover:border-n-brand/30 hover:bg-n-alpha-1 transition-all group"
            >
              <div
                class="flex items-center gap-3 min-w-0 cursor-pointer flex-1"
                @click="setNumberOnly(contact.phoneNumber || contact.phone_number)"
              >
                <Avatar
                  :src="contact.avatar"
                  :name="contact.name || 'Customer'"
                  :size="40"
                  class="shrink-0"
                />
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium text-n-slate-12 truncate">
                    {{ contact.name || 'Unnamed Contact' }}
                  </p>
                  <p class="text-xs text-n-slate-10 truncate">
                    {{ contact.phoneNumber || contact.phone_number || 'No phone number' }}
                  </p>
                </div>
              </div>

              <!-- Quick Dial Button -->
              <Button
                v-if="contact.phoneNumber || contact.phone_number"
                icon="i-lucide-phone"
                teal
                size="sm"
                class="!rounded-xl shrink-0"
                @click="setNumberAndPromptCall(contact.phoneNumber || contact.phone_number)"
              />
            </div>
          </div>
        </div>
      </section>
        </div>
      </div>
    </main>

    <!-- Outbound Number Selection Dialog (Triggered after clicking Call if multiple account numbers exist) -->
    <Dialog
      v-if="showSimPickerDialog"
      title="Select Outbound Number"
      :description="`Choose which number you want to initiate the call to ${phoneNumber} from:`"
      :show-confirm-button="false"
      cancel-button-label="Cancel"
      @cancel="showSimPickerDialog = false"
    >
      <div class="flex flex-col gap-2.5 py-2">
        <div
          v-for="inbox in voiceInboxes"
          :key="inbox.id"
          class="flex items-center justify-between p-3.5 rounded-2xl border bg-n-surface-1 border-n-weak hover:border-n-teal-9 hover:bg-n-teal-3/10 transition-all cursor-pointer group"
          @click="selectNumberAndCall(inbox)"
        >
          <div class="flex items-center gap-3.5 min-w-0">
            <div class="flex items-center justify-center size-10 rounded-xl bg-n-teal-3 text-n-teal-11 font-bold text-xs shrink-0 group-hover:scale-105 transition-transform">
              <Icon icon="i-lucide-phone-outgoing" class="size-5" />
            </div>
            <div class="min-w-0">
              <p class="text-sm font-semibold text-n-slate-12 truncate">
                {{ inbox.displayNumber || inbox.name }}
              </p>
              <p class="text-xs text-n-slate-10 truncate">
                {{ inbox.name }}
              </p>
            </div>
          </div>

          <div class="flex items-center gap-2 shrink-0">
            <Button
              teal
              size="sm"
              icon="i-lucide-phone"
              label="Call with this number"
              class="!rounded-xl"
              @click.stop="selectNumberAndCall(inbox)"
            />
          </div>
        </div>
      </div>
    </Dialog>
  </section>
</template>
